import React, { useCallback, useEffect, useState } from 'react';
import { useHtmlLang } from '@ds/react-utils';
import { setLang } from '@ds/base';
import { apiService, configuration, monitoring } from 'services';
import { IConfigurationInitResponse } from 'services/Configuration';

import './App.css';
import { Branding, defaultBranding } from 'store/types/branding';
import { buildCustomBranding } from 'utils/brandingUtils';
import { BrowserRouter } from 'react-router-dom';
import { ApplicationLayout } from '../layouts';
import { VerifyApplicationContent, VerifyApplicationFooter, VerifyApplicationHeader } from './partialsInk';
import VerifyApplicationContext, { IVerifyApplicationContext } from 'contexts/VerifyApplicationContext';

function App() {
    const [context, setContext] = useState<IVerifyApplicationContext>({
        configurationState: 'Loading',
        displayChannel: 'SMSandPhoneCall',
        isDocumentLocked: false,
        phoneNumberDigitCheck: 'None',
        allowRecipientProvidedPhoneNumber: false,
        language: useHtmlLang() || 'en',
        branding: defaultBranding,
        customBranding: undefined,
        exception: undefined,
        updateLanguage: undefined,
    });
    const [loading, setLoading] = useState<Boolean>(false);

    const updateLanguage = useCallback((newLanguage: string) => {
        setContext(prevContext => ({
            ...prevContext,
            language: newLanguage
        }));
    }, []);

    /**
     * Configure custom application branding
     */
    const loadAppBranding = useCallback(async (): Promise<Branding> => {
        try {
            const branding = await apiService.Get<Branding>('/branding');

            if (!branding) {
                return defaultBranding;
            }

            return branding;
        } catch (err) {
            // We do nothing and keep the default branding
            return defaultBranding;
        }
    }, []);

    const setLocale = (locale: any) => {
        setLang(locale);
    };

    useEffect(() => {
        if (!loading) {
            setLoading(true);

            // only let the application proceed when both configuration, branding, and phone number format are resolved
            Promise.all([configuration.initialize(), loadAppBranding()]).then((value: [IConfigurationInitResponse, Branding]) => {
                let config: IConfigurationInitResponse;
                let branding: Branding;
                [config, branding] = value;

                if (branding?.language) {
                    setLocale(branding.language);
                }

                monitoring.initialize(branding?.language)

                const customBranding = buildCustomBranding(branding?.colors, window.phone826BrandingFix);

                setContext({
                    configurationState: config.state,
                    displayChannel: config.supportedDisplayChannel,
                    isDocumentLocked: config.state === 'Locked',
                    phoneNumberDigitCheck: config.phoneNumberDigitCheck,
                    allowRecipientProvidedPhoneNumber: config.allowRecipientProvidedPhoneNumber,
                    language: branding?.language || context.language,
                    branding: branding,
                    customBranding,
                    exception: config.exception,
                    updateLanguage: updateLanguage,
                });
            });
        }
    }, [context, loadAppBranding, loading]);

    if (context.configurationState === 'Loading') {
        return <></>;
    }

    return (
        <VerifyApplicationContext.Provider value={context}>
            <BrowserRouter>
                <ApplicationLayout
                    header={VerifyApplicationHeader}
                    content={VerifyApplicationContent}
                    footer={VerifyApplicationFooter}
                    accountBranding={context.customBranding}
                />
            </BrowserRouter>
        </VerifyApplicationContext.Provider>
    );
}

export default App;
